import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { allSettled, fork, serialize } from 'effector';
import { useUnit } from 'effector-react';
import { RiArrowRightLine, RiArrowRightUpLine } from 'react-icons/ri';

import { MainLayout } from '@/layouts/main-layout';

import { categoriesQuery } from '@/entities/catalog/model';

import { CategoryGrid } from '@/shared/components/precision/category-grid';
import { Consultation } from '@/shared/components/precision/consultation';
import { EquipmentImage } from '@/shared/components/precision/equipment-image';
import {
  HomepageAbout,
  HomepageDeliveries,
  HomepageFaq,
  HomepageShowcase,
  type HomepageContent,
} from '@/shared/components/precision/homepage-content';
import homepageDefaults from '@/shared/config/homepage-defaults.json';
import { EFFECTOR_STATE_KEY } from '@/shared/lib/effector';
import { PageSeo } from '@/shared/lib/seo';

import { indexPage } from './index.model';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const scope = fork();
  const [, homepage] = await Promise.all([
    allSettled(indexPage.open, { scope, params: {} }),
    fetch(process.env.NEXT_PUBLIC_API_ENDPOINT + '/api/v1/homepage', { signal: AbortSignal.timeout(3000) })
      .then((response) => (response.ok ? response.json() : homepageDefaults))
      .catch(() => homepageDefaults),
  ]);
  return {
    props: {
      [EFFECTOR_STATE_KEY]: serialize(scope),
      homepage,
    },
  };
};

export default function HomePage({ homepage = homepageDefaults }: { homepage: HomepageContent }) {
  const categories = useUnit(categoriesQuery.$data);
  return (
    <>
      <PageSeo
        title="Станки и промышленное оборудование"
        description="Дом Станков. Оборудование для металлообработки, деревообработки и мебельного производства. Подбор и поставка по России."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Дом Станков',
            url: 'https://domstankov.com',
            telephone: '+7-343-363-05-07',
            email: 'zakaz@domstankov.com',
          }).replace(/</g, '\u003c'),
        }}
      />
      <MainLayout>
        <section className="precision-hero">
          <div className="precision-hero__inner container">
            <div className="precision-hero__copy">
              <p className="eyebrow">
                <span />
                ОБОРУДОВАНИЕ ДЛЯ ПРОИЗВОДСТВА
              </p>
              <h1>
                Точность
                <br />
                начинается
                <br />с выбора<span>.</span>
              </h1>
              <p className="precision-hero__subtitle">
                Станки для металла, дерева
                <br />и мебельного производства.
              </p>
              <div className="hero-actions">
                <Link className="precision-button" href="/catalog">
                  Открыть каталог
                  <RiArrowRightLine />
                </Link>
                <a className="text-link" href="#consultation">
                  Подобрать станок
                  <RiArrowRightUpLine />
                </a>
              </div>
            </div>
            <div className="precision-hero__visual">
              <span className="hero-guide hero-guide--horizontal" aria-hidden />
              <span className="hero-guide hero-guide--vertical" aria-hidden />
              <EquipmentImage
                src="/images/precision/hero-lathe.png"
                alt="Иллюстрация современного металлообрабатывающего станка"
                fill
                priority
                sizes="(max-width: 700px) 100vw, 65vw"
              />
              <span className="hero-coordinate" aria-hidden>
                DS / 01
              </span>
            </div>
            <div className="hero-baseline">
              <span>Металл. Дерево. Ваше производство.</span>
              <a href="#directions">
                Найти своё направление <span>↓</span>
              </a>
            </div>
          </div>
        </section>
        <section className="directions-section container" id="directions">
          <div className="section-heading">
            <div>
              <p className="eyebrow">КАТАЛОГ ОБОРУДОВАНИЯ</p>
              <h2>Что вы производите?</h2>
            </div>
            <p>
              От отдельной операции
              <br />
              до задач вашего производства.
            </p>
            <Link className="text-link" href="/catalog">
              Весь каталог
              <RiArrowRightLine />
            </Link>
          </div>
          <CategoryGrid categories={categories?.data || []} imagery="studio" />
        </section>
        <HomepageShowcase products={homepage.products} />
        <HomepageAbout about={homepage.about} />
        <HomepageDeliveries cases={homepage.cases} />
        <section className="process-section container">
          <div className="process-intro">
            <p className="eyebrow">ОТ ЗАДАЧИ К РЕШЕНИЮ</p>
            <h2>
              Разберёмся
              <br />в деталях.
            </h2>
          </div>
          {[
            {
              number: '01',
              title: 'Обсудим задачу',
              text: 'Материал, операции, размеры заготовок и требования к результату.',
            },
            {
              number: '02',
              title: 'Подберём оборудование',
              text: 'Сравним подходящие модели и важные для вас характеристики.',
            },
            {
              number: '03',
              title: 'Согласуем поставку',
              text: 'Обсудим комплектацию, стоимость и доставку в ваш регион.',
            },
          ].map((step) => (
            <div className="process-step" key={step.number}>
              <span>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          ))}
        </section>
        <HomepageFaq faq={homepage.faq} />
        <Consultation />
      </MainLayout>
    </>
  );
}
